import React, { useState, useEffect } from 'react';
import { Globe, TrendingUp, Users, Clock } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import TimeRangeSelector from '../../components/analytics/TimeRangeSelector';
import MetricCard from '../../components/analytics/MetricCard';
import DataTable from '../../components/analytics/DataTable';
import WorldMap from '../../components/analytics/WorldMap';
import { useGA4 } from '../../contexts/GA4Context';
import { formatNumber, formatDuration, formatPercentage, formatDate } from '../../utils/analytics-helpers';
import './TrafficAnalytics.css';

const TrafficAnalyticsServer = () => {
  const {
    loading,
    error,
    metrics,
    sources,
    pages,
    countries,
    timeSeries,
    refresh
  } = useGA4();

  const [activeTab, setActiveTab] = useState('overview');

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div style={{
        background: 'white',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#111827' }}>
          {formatDate(label, 'short')}
        </p>
        {payload.map((entry, index) => (
          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: entry.color }} />
            <span style={{ fontSize: '14px', color: '#6B7280' }}>
              {entry.name}: <strong style={{ color: '#111827' }}>{formatNumber(entry.value)}</strong>
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Define columns for tables
  const sourcesColumns = [
    { key: 'source', label: 'Source', sortable: true },
    { key: 'users', label: 'Users', sortable: true, format: (val) => formatNumber(val) },
    { key: 'sessions', label: 'Sessions', sortable: true, format: (val) => formatNumber(val) }
  ];

  const pagesColumns = [
    { key: 'pagePath', label: 'Page Path', sortable: true },
    { key: 'pageViews', label: 'Views', sortable: true, format: (val) => formatNumber(val) },
    { key: 'avgSessionDuration', label: 'Avg Duration', sortable: true, format: (val) => formatDuration(val) },
    { key: 'engagementRate', label: 'Engagement', sortable: true, format: (val) => formatPercentage(val) }
  ];

  const countriesColumns = [
    { key: 'country', label: 'Country', sortable: true },
    { key: 'users', label: 'Users', sortable: true, format: (val) => formatNumber(val) }
  ];

  return (
    <div className="traffic-analytics">
      <div className="traffic-header">
        <div>
          <h2>Traffic Analytics</h2>
          <p className="traffic-subtitle">Google Analytics 4 - Real-time data</p>
        </div>
        <TimeRangeSelector />
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading analytics data...</p>
        </div>
      ) : (
        <>
          {/* Metrics Overview */}
          <div className="metrics-grid">
            <MetricCard
              title="Total Users"
              value={metrics?.activeUsers || 0}
              previousValue={metrics?.previousActiveUsers}
              format="number"
              icon={Users}
              subtitle="Active users"
            />
            <MetricCard
              title="New Users"
              value={metrics?.newUsers || 0}
              previousValue={metrics?.previousNewUsers}
              format="number"
              icon={TrendingUp}
              subtitle="First-time visitors"
            />
            <MetricCard
              title="Sessions"
              value={metrics?.sessions || 0}
              previousValue={metrics?.previousSessions}
              format="number"
              icon={Globe}
              subtitle="Total visits"
            />
            <MetricCard
              title="Avg Session Duration"
              value={metrics?.avgSessionDuration || 0}
              previousValue={metrics?.previousAvgSessionDuration}
              format="duration"
              icon={Clock}
              subtitle="Time per session"
            />
          </div>

          {/* Traffic Trend Chart */}
          {timeSeries && timeSeries.length > 0 && (
            <div className="chart-container">
              <h3>Traffic Trends</h3>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={timeSeries}>
                  <defs>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: '#6B7280', fontSize: 12 }}
                    tickFormatter={(value) => formatDate(value, 'short')}
                  />
                  <YAxis
                    tick={{ fill: '#6B7280', fontSize: 12 }}
                    tickFormatter={(value) => formatNumber(value)}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{
                      paddingTop: '20px',
                      fontSize: '14px',
                      fontFamily: 'var(--font-body)'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="activeUsers"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    fill="url(#colorUsers)"
                    name="Active Users"
                  />
                  <Area
                    type="monotone"
                    dataKey="sessions"
                    stroke="#8B5CF6"
                    strokeWidth={2}
                    fill="url(#colorSessions)"
                    name="Sessions"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Tabs */}
          <div className="traffic-tabs">
            <button
              className={activeTab === 'overview' ? 'active' : ''}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={activeTab === 'sources' ? 'active' : ''}
              onClick={() => setActiveTab('sources')}
            >
              Traffic Sources
            </button>
            <button
              className={activeTab === 'pages' ? 'active' : ''}
              onClick={() => setActiveTab('pages')}
            >
              Top Pages
            </button>
            <button
              className={activeTab === 'geography' ? 'active' : ''}
              onClick={() => setActiveTab('geography')}
            >
              Geography
            </button>
          </div>

          {/* Tab Content */}
          <div className="traffic-content">
            {activeTab === 'overview' && (
              <div className="overview-grid">
                <div className="overview-section">
                  <h3>Top Traffic Sources</h3>
                  <DataTable
                    data={sources || []}
                    columns={sourcesColumns}
                    searchable={false}
                    exportable={false}
                    pageSize={5}
                  />
                </div>

                <div className="overview-section">
                  <h3>Top Pages</h3>
                  <DataTable
                    data={pages || []}
                    columns={pagesColumns.slice(0, 2)}
                    searchable={false}
                    exportable={false}
                    pageSize={5}
                  />
                </div>

                <div className="overview-section">
                  <h3>Top Countries</h3>
                  <DataTable
                    data={countries || []}
                    columns={countriesColumns}
                    searchable={false}
                    exportable={false}
                    pageSize={5}
                  />
                </div>
              </div>
            )}

            {activeTab === 'sources' && (
              <DataTable
                data={sources || []}
                columns={sourcesColumns}
                title="Traffic Sources"
                searchable={true}
                exportable={true}
                pageSize={25}
              />
            )}

            {activeTab === 'pages' && (
              <DataTable
                data={pages || []}
                columns={pagesColumns}
                title="Page Performance"
                searchable={true}
                exportable={true}
                pageSize={25}
              />
            )}

            {activeTab === 'geography' && (
              <div className="geography-view">
                <WorldMap data={countries || []} />
                <div style={{ marginTop: 'var(--spacing-lg)' }}>
                  <DataTable
                    data={countries || []}
                    columns={countriesColumns}
                    title="Detailed Country Breakdown"
                    searchable={true}
                    exportable={true}
                    pageSize={25}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TrafficAnalyticsServer;
